import { useState } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { generateMessage } from "../applicationAgentApi";

const getRowState = (coApplicant) => {
  const link = coApplicant.pendingMagicLink;
  if (link.status === "consumed")
    return { selectable: false, badge: "Already accepted" };
  if (link.status === "expired")
    return { selectable: false, badge: "Invite expired" };
  if (link.status === "pending")
    return { selectable: true, badge: "Pending" };
  return { selectable: false, badge: link.status };
};

const ResendInviteSubform = ({
  alert,
  mcpToken,
  suggestedMessage,
  recipients,
  onRecipientsChange,
  onSuggestedMessageChange,
  submitting,
  disabled,
  classes,
}) => {
  const [opsGuidance, setOpsGuidance] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  const coApplicants = alert.coApplicants || [];
  const selectedLinkIds = new Set(recipients.map((r) => r.pendingMagicLinkId));

  const toggle = (coApplicant) => {
    const linkId = coApplicant.pendingMagicLink.id;
    const next = new Set(selectedLinkIds);
    if (next.has(linkId)) {
      next.delete(linkId);
    } else {
      next.add(linkId);
    }
    const nextRecipients = coApplicants
      .filter((ca) => next.has(ca.pendingMagicLink.id))
      .map((ca) => ({
        applicantId: ca.applicantId,
        pendingMagicLinkId: ca.pendingMagicLink.id,
        name: ca.name,
        email: ca.email,
      }));
    onRecipientsChange(nextRecipients);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateError(null);
    try {
      const result = await generateMessage(mcpToken, {
        sentimentAnalysisId: alert.sentimentAnalysisId,
        actionCategory: "resent_invite",
        context: {
          recipients: recipients.map((r) => ({
            name: r.name,
            email: r.email,
          })),
          ops_guidance: opsGuidance.trim() || undefined,
        },
      });
      onSuggestedMessageChange(result.suggested_message);
    } catch (err) {
      setGenerateError(err.message || "Failed to generate message");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <div className={classes.generateSection}>
        <Typography className={classes.label} variant="subtitle2">
          Co-applicants
        </Typography>
        {coApplicants.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No co-applicants on this application.
          </Typography>
        ) : (
          coApplicants.map((ca) => {
            const { selectable, badge } = getRowState(ca);
            return (
              <Box
                key={ca.pendingMagicLink.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                py={0.5}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedLinkIds.has(ca.pendingMagicLink.id)}
                      onChange={() => toggle(ca)}
                      disabled={!selectable || submitting || disabled}
                      color="primary"
                    />
                  }
                  label={
                    <span>
                      {ca.name ? (
                        <>
                          <strong>{ca.name}</strong>{" "}
                          <span style={{ color: "#777" }}>{ca.email}</span>
                        </>
                      ) : (
                        <strong>{ca.email}</strong>
                      )}
                    </span>
                  }
                />
                <Typography variant="caption" color="textSecondary">
                  {badge}
                </Typography>
              </Box>
            );
          })
        )}
      </div>

      <div className={classes.generateSection}>
        <Typography className={classes.label} variant="subtitle2">
          Suggested Response
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          label="Guidance for response (optional)"
          value={opsGuidance}
          onChange={(e) => setOpsGuidance(e.target.value)}
          disabled={submitting || disabled}
          placeholder="Describe what the response should say — leave blank to generate from conversation context alone"
          style={{ marginBottom: 8 }}
        />
        <Box mb={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleGenerate}
            disabled={
              generating ||
              submitting ||
              disabled ||
              !mcpToken ||
              recipients.length === 0
            }
          >
            {generating ? (
              <>
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                Generating...
              </>
            ) : (
              "Generate Suggested Response"
            )}
          </Button>
        </Box>
        {generateError && (
          <Alert severity="error" style={{ marginBottom: 8 }}>
            {generateError}
          </Alert>
        )}
        <TextField
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          label="Message to applicant"
          value={suggestedMessage}
          onChange={(e) => onSuggestedMessageChange(e.target.value)}
          disabled={submitting || disabled}
          placeholder="Click 'Generate Suggested Response' or type a message manually"
        />
      </div>
    </>
  );
};

export default ResendInviteSubform;
